import React, { useState } from 'react';
import { contactApiService } from '@metlas/contact';
import './LandingPageV2.css';
import './ContactPage.css';
import { SubpageHeader, SubpageFooter } from './SubpageChrome';

/* /contact — modeled on sawbridgeai.com/contact. Posts to /api/v1/contact like the waitlist.
   /contact?role=<title> comes from the careers page and prefills the message for that role. */

const roleFromUrl = (): string => {
  if (typeof window === 'undefined') return '';
  return (new URLSearchParams(window.location.search).get('role') ?? '').slice(0, 80);
};

const POINTS = [
  'Develop or manage real estate: we’ll match you to the right agents',
  'Free trials and early access',
  'Applying for a role? Tell us about yourself and link your work',
];

const ContactPage: React.FC = () => {
  const [role] = useState(roleFromUrl);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState(() =>
    !role ? '' : role === 'Open application' ? 'I’d like to work at Metlas. ' : `I’m applying for the ${role} role. `
  );
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setError('');
    try {
      const res = await contactApiService.submit({
        name: [firstName, lastName].filter(Boolean).join(' '),
        email,
        inquiry: message,
        source: role ? `careers: ${role}` : 'contact',
      });
      if (res.error) {
        setStatus('error');
        setError(res.error);
        return;
      }
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  const busy = status === 'submitting';

  return (
    <div className="mlp-page">
      <SubpageHeader />
      <main>
        <section className="mlp-contact">
          <div className="mlp-wrap mlp-contact-inner">
            <div className="mlp-contact-text">
              <p className="mlp-kicker mlp-kicker--kb">{role ? 'Careers' : 'Contact'}</p>
              <h1 className="mlp-contact-title">Let’s talk.</h1>
              {role && <p className="mlp-contact-role">Applying for: {role}</p>}
              <ul className="mlp-contact-points">
                {POINTS.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>

            <div className="mlp-card">
              {status === 'success' ? (
                <div className="mlp-form mlp-form--done" role="status">
                  <h3>Thanks, we got it.</h3>
                  <p>We’ll get back to you at {email}.</p>
                </div>
              ) : (
                <form className="mlp-form" onSubmit={onSubmit}>
                  <div className="mlp-form-grid">
                    <label>
                      <span>First name *</span>
                      <input
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                        disabled={busy}
                        autoComplete="given-name"
                      />
                    </label>
                    <label>
                      <span>Last name</span>
                      <input
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        disabled={busy}
                        autoComplete="family-name"
                      />
                    </label>
                    <label className="mlp-form-wide">
                      <span>Email *</span>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={busy}
                        autoComplete="email"
                      />
                    </label>
                    <label className="mlp-form-wide">
                      <span>Message *</span>
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        required
                        maxLength={2000}
                        rows={6}
                        disabled={busy}
                        placeholder="Tell us about your project, your portfolio, or yourself."
                      />
                    </label>
                  </div>
                  {status === 'error' && <p className="mlp-form-error">{error}</p>}
                  <button type="submit" className="mlp-btn mlp-btn--primary" disabled={busy}>
                    {busy ? 'Sending…' : 'Send message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
        <SubpageFooter />
      </main>
    </div>
  );
};

export default ContactPage;
